import { createReducer, on } from '@ngrx/store';
import * as uuid from 'uuid';
import { SORT_ORDER } from '../prime-commitments/prime-commitments.types';
import { primeChangesSidebarActions } from '../prime-commitments/prime-commitments.actions';
import { pcoActions } from './pco.actions';
import { pcoFeatureKey } from './pco.constants';
import {
  PCO_STATUS,
  PcoContractWithChangeOrder,
  PcoDetail,
  PcoLogItem,
  PcoSortingField,
} from './pco.types';
import { viewProjectActions } from '@app/store/view-project/view-project.actions';
import { pcoListingActions } from '@app/pages/webapp/projects/view-project/pco-listing/pco-listing.component.actions';

export { pcoFeatureKey };

export interface PcoState {
  contractsWithChangeOrders: PcoContractWithChangeOrder[];
  loading: boolean;
  error?: string | null;

  nextPcoNumber: string | null;
  isPcoNumberValid: boolean;

  pcoListing: {
    allPcoLogs: PcoLogItem[];
    loadedOnce: boolean;
    loading: boolean;
    search: string;
    statuses: PCO_STATUS[];
    lastPage: number | null;
    lastPageEmpty: boolean;
    sortBy: PcoSortingField | null;
    sortDirection: SORT_ORDER | null;
  };

  pcoDetails: {
    wasLoadedOnce: boolean;
    isLoading: boolean;
    sidebar: PcoDetail | null;
    autosaveStatus: 'synced' | 'error' | 'pending' | 'syncing';
  };
}

export const initialPcoState: PcoState = {
  contractsWithChangeOrders: [],
  loading: false,
  error: null,

  nextPcoNumber: null,
  isPcoNumberValid: true,

  pcoListing: {
    allPcoLogs: [],
    loadedOnce: false,
    loading: true,
    search: '',
    statuses: [],
    lastPage: null,
    lastPageEmpty: false,
    sortBy: null,
    sortDirection: null,
  },
  pcoDetails: {
    wasLoadedOnce: false,
    isLoading: true,
    sidebar: null,
    autosaveStatus: 'synced',
  },
};

export const pcoReducer = createReducer(
  initialPcoState,
  on(viewProjectActions.selectedProjectChanged, (): PcoState => {
    return {
      ...initialPcoState,
    };
  }),

  on(
    pcoActions.loadContractsWithChangeOrders,
    (state): PcoState => ({ ...state, loading: true, error: null }),
  ),
  on(pcoActions.loadContractsWithChangeOrdersSuccess, (state, action): PcoState => {
    return {
      ...state,
      contractsWithChangeOrders: action.contractsWithChangeOrders,
      loading: false,
      error: null,
    };
  }),
  on(
    pcoActions.loadContractsWithChangeOrdersFailure,
    (state, action): PcoState => ({
      ...state,
      loading: false,
      error: action.error,
    }),
  ),

  // -----------------------------------------------------
  // PCO Number Validation
  // -----------------------------------------------------

  on(pcoActions.nextPcoNumberLoadedSuccessfully, (state, action): PcoState => {
    return {
      ...state,
      nextPcoNumber: action.nextPcoNumber,
    };
  }),

  on(pcoActions.PCONumberValidated, (state, action): PcoState => {
    return {
      ...state,
      isPcoNumberValid: action.isValid,
    };
  }),

  // -----------------------------------------------------
  // PCO Listing Actions
  // -----------------------------------------------------

  on(pcoListingActions.onPCOListingAccess, (state): PcoState => {
    return {
      ...state,
      pcoListing: {
        ...state.pcoListing,
        loading: !state.pcoListing.allPcoLogs.length,
      },
    };
  }),

  on(pcoActions.loadPcoListingSuccess, (state, action): PcoState => {
    let allPCOLogs: PcoLogItem[] = [];
    if (action.requestMetadata.page === 1) {
      allPCOLogs = [...action.PCOs];
    } else {
      allPCOLogs = [...state.pcoListing.allPcoLogs, ...action.PCOs];
    }

    return {
      ...state,
      pcoListing: {
        ...state.pcoListing,
        lastPage: action.requestMetadata.page,
        allPcoLogs: allPCOLogs,
        loading: false,
        loadedOnce: true,
        lastPageEmpty: action.PCOs?.length === 0,
      },
    };
  }),

  on(pcoActions.loadPcoListingFailed, (state): PcoState => {
    return {
      ...state,
      pcoListing: {
        ...state.pcoListing,
        loading: false,
        loadedOnce: true,
      },
    };
  }),

  on(pcoListingActions.onPCOListingSearch, (state, action): PcoState => {
    return {
      ...state,
      pcoListing: {
        ...state.pcoListing,
        loading: true,
        search: action.search,
        lastPageEmpty: false, // Reset lastPageEmpty when a new search is performed
      },
    };
  }),

  on(pcoListingActions.onPCOListingStatusChange, (state, action): PcoState => {
    return {
      ...state,
      pcoListing: {
        ...state.pcoListing,
        loading: true,
        statuses: action.statuses,
        lastPageEmpty: false, // Reset lastPageEmpty when a new search is performed
      },
    };
  }),

  on(pcoListingActions.onPCOListingNextPageNeeded, (state): PcoState => {
    return {
      ...state,
      pcoListing: {
        ...state.pcoListing,
        loading: !state.pcoListing.lastPageEmpty,
      },
    };
  }),

  on(pcoListingActions.onPCOSortingChange, (state, action): PcoState => {
    return {
      ...state,
      pcoListing: {
        ...state.pcoListing,
        loading: true,
        sortBy: action.sortBy,
        sortDirection: action.sortDirection,
      },
    };
  }),

  on(
    pcoActions.onPCOSubmit,
    pcoActions.onPCOChangeActionFirstPageSync,
    primeChangesSidebarActions.onCORSaved,
    (state): PcoState => {
      return {
        ...state,
        pcoListing: {
          ...state.pcoListing,
          loading: true,
        },
      };
    },
  ),

  on(pcoActions.onFailedToSavePCO, (state): PcoState => {
    return {
      ...state,
      pcoListing: {
        ...state.pcoListing,
        loading: false,
      },
    };
  }),

  // -----------------------------------------------------
  // PCO Details Actions
  // -----------------------------------------------------

  on(pcoActions.PCODetailsLoaded, (state, action): PcoState => {
    return {
      ...state,
      pcoDetails: {
        sidebar: {
          ...action.response,
        },
        wasLoadedOnce: true,
        isLoading: false,
        autosaveStatus: 'synced',
      },
    };
  }),

  on(pcoActions.onSyncPCODetailsTriggered, (state): PcoState => {
    return {
      ...state,
      pcoDetails: {
        ...state.pcoDetails,
        autosaveStatus: 'syncing',
      },
    };
  }),

  on(pcoActions.onFailedToSavePCODetails, (state): PcoState => {
    return {
      ...state,
      pcoDetails: {
        ...state.pcoDetails,
        autosaveStatus: 'error',
        isLoading: false,
      },
    };
  }),

  on(pcoActions.onPcoScoRequested, (state): PcoState => {
    return {
      ...state,
      pcoDetails: {
        ...state.pcoDetails,
        isLoading: false,
      },
    };
  }),

  on(
    pcoActions.onPCODetailsLocalStateUpdate,
    pcoActions.onPCOLineItemAdded,
    (state, action): PcoState => {
      return {
        ...state,
        pcoDetails: {
          ...state.pcoDetails,
          sidebar: {
            ...state.pcoDetails.sidebar,
            ...action.PCO,
          },
          autosaveStatus: action.PCO.pco_items.some((item) => uuid.validate(item.id))
            ? 'pending'
            : state.pcoDetails.autosaveStatus,
        },
      };
    },
  ),

  on(pcoActions.onPCOMarkupToggled, (state, action): PcoState => {
    return {
      ...state,
      pcoDetails: {
        ...state.pcoDetails,
        sidebar: {
          ...state.pcoDetails.sidebar,
          markups_enabled: action.isChecked,
          pco_markups: state.pcoDetails.sidebar.pco_markups.map((item) => ({
            ...item,
            editMode: false,
          })),
        },
      },
    };
  }),

  on(pcoActions.onPCOInvalid, (state): PcoState => {
    return {
      ...state,
      pcoDetails: {
        ...state.pcoDetails,
        autosaveStatus: 'pending',
      },
    };
  }),

  on(pcoActions.onPCODetailsSidebarClosed, (state, action): PcoState => {
    return {
      ...state,
      pcoDetails: {
        ...initialPcoState.pcoDetails,
        sidebar: action.editChangeActionTriggered ? state.pcoDetails.sidebar : null,
      },
    };
  }),

  on(pcoActions.onSyncPCODetails, pcoActions.onPCORequestSCO, (state): PcoState => {
    return {
      ...state,
      pcoDetails: {
        ...state.pcoDetails,
        isLoading: true,
      },
    };
  }),
);
